// Phase G G5 / Native Studio Inc 1 — subscribe-only watch stage. NEVER captures
// media (video={false} audio={false}); renders the BroadcastView program panel
// (all publishers) + the read-only chat. Reused for the host self-preview
// (withChat=false, muted).

import { LiveKitRoom, RoomAudioRenderer, StartAudio } from '@livekit/components-react';
import '@livekit/components-styles';
import { Volume2, VolumeX } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { BroadcastView } from './BroadcastView';
import { SocialChatPanel } from './SocialChatPanel';
import { FeaturedSuperchatProvider } from './useFeaturedSuperchat';

export function ViewerStage({
  token,
  serverUrl,
  roomStatus,
  hostId,
  title,
  tickerLines,
  logoUrl,
  withChat = true,
  muted = false,
  guestBar,
}: {
  token: string;
  serverUrl: string;
  roomStatus: string;
  hostId: string;
  title: string;
  tickerLines: string[];
  logoUrl?: string | null;
  withChat?: boolean;
  /** Host self-preview: render video only, no audio playback or unmute control. */
  muted?: boolean;
  /** Guest queue bar (Inc 4a) — a slim strip at the foot of the video column. */
  guestBar?: ReactNode;
}) {
  // Viewer-controlled audio mute on the public page (separate from `muted`, which
  // is the silent host self-preview). Audio plays by default; this lets a viewer
  // silence it.
  const [audioMuted, setAudioMuted] = useState(false);
  return (
    <LiveKitRoom
      token={token}
      serverUrl={serverUrl}
      connect
      video={false}
      audio={false}
      options={{ adaptiveStream: true }}
      data-lk-theme="default"
      className="h-full w-full"
    >
      <FeaturedSuperchatProvider>
        {/* On desktop the row collapses to the video's 16:9 height (md:h-auto +
          the video's md:aspect-video), so the chat — stretched to the row — ends
          at the bottom of the video stream rather than the full viewport. Mobile
          keeps the stacked full-height layout. */}
        <div className="flex h-full w-full flex-col md:h-auto md:flex-row">
          <div className="relative flex-1 overflow-hidden bg-black md:aspect-video md:self-start">
            {/* BroadcastView frames itself: a centered, contained 16:9 program
              normally, or a full-bleed presentation layout (cameras in a left
              column, shared content filling the rest) while a screen-share is
              on air. */}
            <BroadcastView
              roomStatus={roomStatus}
              hostId={hostId}
              title={title}
              tickerLines={tickerLines}
              logoUrl={logoUrl}
            />
            {!muted && (
              <div
                className={`absolute right-4 z-10 flex items-center gap-2 ${guestBar ? 'bottom-14' : 'bottom-4'}`}
              >
                <button
                  type="button"
                  onClick={() => setAudioMuted((m) => !m)}
                  aria-label={audioMuted ? 'Unmute' : 'Mute'}
                  className="rounded-md bg-video-bg/70 p-2 text-video-text transition hover:bg-video-bg/90"
                >
                  {audioMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </button>
                <StartAudio
                  label="Tap to unmute"
                  className="rounded-md bg-video-bg/70 px-3 py-1.5 font-sans text-xs text-video-text"
                />
              </div>
            )}
            {guestBar && <div className="absolute inset-x-0 bottom-0 z-20">{guestBar}</div>}
          </div>
          {withChat && (
            <div className="h-64 w-full shrink-0 border-t border-video-line md:h-auto md:w-80 md:border-l md:border-t-0">
              <SocialChatPanel />
            </div>
          )}
        </div>
        {!muted && <RoomAudioRenderer volume={audioMuted ? 0 : 1} />}
      </FeaturedSuperchatProvider>
    </LiveKitRoom>
  );
}
